import { simpleDate } from 'lib/date';
import { Source } from 'models/AIArtifact';

/**
 * Build the descriptive line shown above the PDF preview in the citation
 * hover card. Returns null when we don't have enough info to say anything
 * useful (e.g., legacy summary missing the provenance fields). Callers
 * should suppress the row entirely on null.
 */
export function formatProvenance(source: Source): string | null {
  const at = source.createdAt ? simpleDate(source.createdAt) : null;
  switch (source.provenance?.type) {
    case 'generated':
      return formatGenerated(at, source.provenance.reason);
    case 'integration':
      return formatIntegration(at, source.provenance.facility);
    case 'user_upload':
      return formatUserUpload(at, source.createdByUser, source.createdByEntity);
    default:
      return null;
  }
}

function formatGenerated(at: string | null, reason: string | undefined): string {
  const base = at ? `Document generated ${at}` : 'Document generated';
  return reason ? `${base} for ${reason}` : base;
}

function formatIntegration(at: string | null, facility: string | undefined): string {
  const base = at ? `Received ${at}` : 'Received via integration';
  return facility ? `${base} from ${facility}` : base;
}

function formatUserUpload(
  at: string | null,
  user: Source['createdByUser'],
  entity: Source['createdByEntity']
): string | null {
  // Mirror the activity precedent: "<user.full_name> at <group.name>".
  // User shown when known; entity appended only when present.
  const who = userPlusEntity(user, entity);
  if (who && at) return `Uploaded by ${who} on ${at}`;
  if (who) return `Uploaded by ${who}`;
  if (at) return `Uploaded ${at}`;
  return null;
}

function userPlusEntity(user: Source['createdByUser'], entity: Source['createdByEntity']): string | null {
  if (user?.fullName && entity?.name) return `${user.fullName} at ${entity.name}`;
  return user?.fullName ?? entity?.name ?? null;
}
